
import { Component, Vue, Watch } from 'vue-property-decorator';
import LogRocket from 'logrocket';
import createApp from '@shopify/app-bridge';
import { isShopifyEmbedded } from '@shopify/app-bridge/utilities';
import { Redirect, History, AppLink, NavigationMenu } from '@shopify/app-bridge/actions';
import { mapActions, mapGetters } from 'vuex';

import {
  get, post, getIdentityToken, setIdentityToken, removeIdentityToken,
  verifyIdentityToken,
} from '@/services/http';
import { EventHandler } from '@/modules/events';
import { MixpanelInitializer } from '@/modules/mixpanelInitializer';
import mixpanel from 'mixpanel-browser';
import { EventBus } from '@/main';

import { PermissionModel } from '@/models/permission';
import { StoreModel } from '@/models/store';
import {UserModel} from '@/models/user';
import { DimensionColumnList } from '@/collections/dimension_columns';
import { TagTypeList } from '@/collections/tag_types';
import { ReportMetadataList } from '@/collections/report_metadatas';
import { TagList } from '@/collections/tags';
import { MeasureList } from '@/collections/measures';
import { LoginRequestList } from '@/collections/login_requests';
import { LoginRequestModel } from '@/models/login_request';
import Alert from '@/components/Alert.vue';
import VerticalMenuBar from '@/components/VerticalMenuBar.vue';
import AppBanner from '@/components/AppBanner.vue';
import { providePermission } from '@/services/PermissionService';

@Component({
  components: {
    SideBarHorizon: () => import('@/components/SideBarHorizon.vue'),
    Login: () => import('@/components/Login.vue'),
    UpgradeVersion: () => import('@/components/UpgradeVersion.vue'),
    Permission: () => import('@/components/Permission.vue'),
    DataPermissionNotice: () => import('@/components/DataPermissionNotice.vue'),
    DataSyncWarning: () => import('@/components/DataSyncWarning.vue'),
    Installing: () => import('@/components/Installing.vue'),
    HorizontalMenu: () => import('@/components/HorizontalMenu.vue'),
    LoadingSlider: () => import('@/components/LoadingSlider.vue'),
    AgentChatFab: () => import('@/components/common/AgentChatFAB.vue'),
    LoginRequestPopup: () => import('@/components/LoginRequestPopup.vue'),
    ReviewRequestDialog: () => import('@/components/ReviewRequestDialog.vue'),
    Alert,
    VerticalMenuBar,
    AppBanner,
  },
  provide() {
    const vm = this as any;
    return {
      store: vm.store,
    };
  },
})

export default class App extends Vue {

  public get getAppMenuBarSize() {
    if (this.isExpandAppMenuBar) {
      return 'view-show-app-menu-bar';
    } else {
      return 'view-hide-app-menu-bar';
    }
  }

  public get isEmbeddedMode() {
    try {
      return isShopifyEmbedded();
    } catch (error) {
      // Return false when not properly embedded (direct access, local dev, etc.)
      return false;
    }
  }

  public get isPassedInstallation() {
    return this.store.passedInstallingFlow !== 'new';
  }

  public get isConfirmingSubscription() {
    return [
      'subscriptionConfirm',
      'autoSubscription',
    ].includes(
      this.$route.name || '',
    );
  }

  public get isWelcomeMode(): boolean {
    return false;
  }

  public get getCanShowUpgradeVersion() {
    const requireUpgradePages = [
      'purchase-order-detail',
    ];

    if (!this.$route.name) {
      return false;
    }

    return requireUpgradePages.includes(this.$route.name);
  }

  public get isAssistantRoute(): boolean {
    return this.$route.path.startsWith('/assistant');
  }

  public isLogined: boolean = false;
  public isRequiredLogin: boolean = false;
  public error: boolean = false;
  public loading: boolean = false;
  public installing: boolean = false;
  public checkInstalling: boolean = false;
  public isCallbackRequest: boolean = false;
  public creating: boolean = false;
  public errorMessage: string = '';
  public shopifyName: string = '';
  public isDataReady: boolean = true;
  public needUpgrade: boolean = false;
  public permission: PermissionModel = new PermissionModel();
  public isPermissionLoading: boolean = true;
  public currentFeature: string = '';
  public store: StoreModel = new StoreModel();
  public isLoggedInFromHades: boolean = false;
  public installationUrls: string[] = [
    'onboard',
    'onboardContact',
    'onboardMessaging',
    'onboardReport',
    'onboardPurpose',
    'onboardComplete',
    'onboardPricing',
    'slackCallback',
    'subscriptionConfirm',
    'autoSubscription',
    'dataImporting',
    'StockySwitcher',
    'StockyDataMigration',
  ];

  public app: any = null;
  public apiKey: string = String(process.env.VUE_APP_API_KEY);
  public host: string = '';
  public reportMetadatas: ReportMetadataList = new ReportMetadataList();
  public tagTypes: TagTypeList = new TagTypeList();
  public tags: TagList = new TagList();
  public measures: MeasureList = new MeasureList();
  public contentLoading: boolean = false;
  public showFullscreenPopup: boolean = true;
  public isFullLoadFinished: boolean = false;
  public mainRandomKey: number = Math.random();
  public storageSupported: boolean = true;
  public isExpandAppMenuBar: boolean = true;
  public isCallbackGoogle: boolean = false;
  public appSnackbar: boolean = false;
  public appSnackbarTimeout: number = 5000;
  public appSnackbarMessage: string = '';
  public appSnackbarColor: string = '';
  public referenceRoute: any = {};
  public activeDimensions: DimensionColumnList = new DimensionColumnList();
  public appContentMargin: string = '56px';
  private permissionService: any;
  private permissionServiceInitialized: boolean = false;
  private permissionInitTimeout: number | null = null;
  private olapsWarmedThisAppInstance: boolean = false; // Add this line
  public pendingLoginRequest: LoginRequestModel | null = null;
  public showLoginRequestPopup: boolean = false;
  public isChatboxSplitActive: boolean = false;
  public showDataReadyModal: boolean = false;
  private athenaPollingInterval: number | null = null;

  public async created() {
    Vue.prototype.$permissionsInitializing = true;

    // PHASE 1: Initialize Mixpanel SDK immediately (before any async operations)
    // This ensures the SDK is ready for tracking in child components like SubscriptionConfirm
    this.initializeMixpanelSdk();

    this.clearCache();
    try {
      if (!isShopifyEmbedded()) {
        if (!this.isLogined) {
          try {
            // Auto-login for demo environment or Cloudflare tunnel previews
            const isDemoHost = window.location.hostname === 'demo.assisty.ai';
            const isTestHost = window.location.hostname === 'test.assisty.ai';
            const isPreviewHost = window.location.hostname.endsWith('.trycloudflare.com');

            if (isDemoHost) {
              const user: UserModel = new UserModel();
              await user.loginDemo();
              this.$emit('logged-in');
              localStorage.setItem('current_loggedin_email', user.email);
            } else if (isTestHost || isPreviewHost) {
              const user: UserModel = new UserModel();
              await user.loginTest();
              this.$emit('logged-in');
              localStorage.setItem('current_loggedin_email', user.email);
            }
          } catch (e: any) {
            // console.log(e);
          }
        }

        const refererUrl = document.referrer;
        if (refererUrl) {
          const urlObj = new URL(refererUrl);
          const embeddedParam = urlObj.searchParams.get('embedded');
          if (embeddedParam === '1') {
            const idToken = urlObj.searchParams.get('id_token');
            if (idToken) {
              const responseToken: any = await post(
                  `/shopify/identify`, { id_token: idToken },
              );
              if (responseToken.token) {
                setIdentityToken(
                    responseToken.token,
                    responseToken.store.shop_name,
                );
                this.isLogined = true;
                const query = Object.assign({}, this.$route.query);
                this.$router.replace({ query });
                await this.getStoreDetail();
                this.error = false;
              }
            }
          }
        }
      }
    } catch (e) {
      // skipped
    }

    if (this.$route.query.state && this.$route.query.code && this.$route.query.scope && this.$route.query.scope.includes('drive.file')) {
      this.isCallbackGoogle = true;
      let paramCode = `${this.$route.query.code}`;
      paramCode = paramCode.replaceAll('/', 'assisty_slash');
      const redirectUriEmbedded = `https://admin.shopify.com/store/${this.$route.query.state}/apps/assisty/oauth2callback?code=${paramCode}`;
      // @ts-ignore: Unreachable code error
      window.location.assign(redirectUriEmbedded);
    }
    try {
      this.getAppMenuBarStatus();
      localStorage.getItem('language');
    } catch (e) {
      this.storageSupported = false;
      return;
    }

    this.$i18n.locale = 'en';
    this.isLoggedInFromHades = localStorage.getItem('loggedInFromHades') === 'true';
    if (process.env.VUE_APP_ENVIRONMENT === 'production' && !this.isLoggedInFromHades && window.location.hostname !== 'demo.assisty.ai' && !window.location.hostname.endsWith('.trycloudflare.com')) {
      try {
        LogRocket.init('igdczq/assisty');
      } catch {
        // skipped
      }
    }
    this.loading = true;
    this.checkIsRequiredLogin();
    try {
      if (isShopifyEmbedded()) {
        const host = this.getShopifyHost();
        if (host) {
          this.app = createApp({
            apiKey: this.apiKey,
            host: host,
          });
        }
      }
    } catch (error) {
      // Ignore App Bridge errors when not properly embedded
      // This is normal when accessing the app directly (not through Shopify admin)
    }
    if (this.isCallbackRequest) {
      await this.createStore();
    } else {
      if (this.isRequiredLogin) {
        if (!this.isLogined && this.$route.path !== '/') {
          localStorage.setItem('redirect_uri', this.$route.fullPath);
        }
        await this.fullLoad();
        EventBus.$on('current-package-changed', (storeModel: StoreModel) => {
          // Assign the store data passed from the event payload
          // Store is already fetched before emission by updateStoreAndEmitPackageChange()
          this.store = storeModel;
          // Store the Shopify store name for use in other components
          console.log('[App] Current package changed:', this.store);
          if (this.store.shopName) {
            localStorage.setItem('shopifyStoreName', this.store.shopName);
          }
        });
      } else {
        this.isFullLoadFinished = true;
      }
      /* await this.permission.fetch(); */
    }
    this.setupCrisp(null);
    if (this.store && this.store.id) {
      this.initializeMixpanel();
    } else {
      EventBus.$once('store-detail-fetched', () => this.initializeMixpanel());
    }
    this.loading = false;
    EventBus.$on('update-new-store-data', async () => {
      const storeModel = new StoreModel();
      await storeModel.fetch();
      this.store = storeModel;
      // Store the Shopify store name for use in other components
      if (this.store.shopName) {
        localStorage.setItem('shopifyStoreName', this.store.shopName);
      }
      EventBus.$emit('update-new-store-data-completed');
    });
    EventBus.$on('full-load-app', async () => {
      this.mainRandomKey = Math.random();
    });
    EventBus.$on('check-athena-finish-interval', async () => {
      this.checkAthenaFinishInterval();
    });

    // Add these for new logic
    EventBus.$on('vertical-menu-pinned', this.handleVerticalMenuPinned);
    this.checkAthenaFinishInterval();

    // Pause App.vue polling when DataImporting has its own faster polling
    EventBus.$on('etl-polling-started', () => {
      if (this.athenaPollingInterval) {
        clearInterval(this.athenaPollingInterval);
        this.athenaPollingInterval = null;
      }
    });
    EventBus.$on('etl-polling-stopped', () => {
      this.checkAthenaFinishInterval();
    });

    EventBus.$on('redirect-to-google-auth', async () => {
      const scope = 'https://www.googleapis.com/auth/drive.file';
      const clientId = process.env.VUE_APP_GOOGLE_AUTH_CLIENT_ID;
      const redirectUrl = `${process.env.VUE_APP_HOST}/oauth2callback`;
      let url = '';
      if (isShopifyEmbedded()) {
        const state = this.store.shopName.split('.')[0];
        url = `https://accounts.google.com/o/oauth2/auth?state=${state}&scope=${scope}&response_type=code&access_type=offline&prompt=consent&redirect_uri=${redirectUrl}&client_id=${clientId}`;
      } else {
        url = `https://accounts.google.com/o/oauth2/auth?scope=${scope}&response_type=code&access_type=offline&prompt=consent&redirect_uri=${redirectUrl}&client_id=${clientId}`;
      }
      // If your app is embedded inside Shopify, use App Bridge to redirect
      if (isShopifyEmbedded()) {
        const host = this.getShopifyHost();
        if (host) {
          this.app = createApp({
            apiKey: this.apiKey,
            host: host,
          });
          Redirect.create(this.app).dispatch(Redirect.Action.REMOTE, url);
        }
        // Otherwise, redirect using the `window` object
      } else {
        window.location.assign(url);
      }
    });

    EventBus.$on('show-snackbar', async (config: any) => {
      this.appSnackbar = true;
      if (config && config.message) {
        this.appSnackbarMessage = config.message;
      } else if (config && config.text) {
        this.appSnackbarMessage = config.text;
      } else {
        this.appSnackbarMessage = config;
      }
      if (config && config.color) {
        this.appSnackbarColor = config.color;
      } else {
        this.appSnackbarColor = 'deep_sku_blue';
      }
    });

    EventBus.$on('clear-cache', async () => {
      this.clearCache();
    });

    // Listen for chatbox layout changes (split/overlay mode)
    EventBus.$on('app:chatbox-layout-change', this.onChatboxLayoutChange);

    // Load permissions on app startup
    if (this.$store.getters.isAuthenticated) {
      await this.permissionService.loadPermissions();
    }

    // Listen for login/logout events
    this.$store.subscribe((mutation) => {
      if (mutation.type === 'setUser') {
        this.permissionService.loadPermissions();
      } else if (mutation.type === 'logout') {
        this.permissionService.clearCache();
      }
    });

    // Initialize permissions when app is created (if user is already logged in)
    if (this.isLogined && this.store && this.store.id) {
     await this.initializePermissionService();
    }

    // Add this in the created() method to listen for permission events
    EventBus.$on('permission-denied', (data: { feature: string, route: string }) => {
      this.isPermissionLoading = false;
      this.currentFeature = data.feature;
    });

    // Add this after initializing the permission service
    EventBus.$on('check-permission', async (data: { permission: string, route: any }) => {
      // Ensure permission service is initialized
      if (!this.permissionServiceInitialized && this.store && this.store.id) {
        await this.initializePermissionService();
      }

      // Check permission using our service
      if (this.permissionService) {
        const hasPermission = this.permissionService.hasPermission(data.permission);
        if (!hasPermission) {
          this.isPermissionLoading = false;
          this.currentFeature = data.permission;
          EventBus.$emit('permission-denied', {
            feature: data.permission,
            route: data.route.path,
          });
        }
      }
    });
  }

  // Method to handle vertical menu pinning state
  public handleVerticalMenuPinned(isPinned: boolean) {
    this.appContentMargin = isPinned ? '300px' : '56px';
    localStorage.setItem('isPinMenu', isPinned ? 'true' : 'false');
  }

  // Method to handle chatbox layout changes (split-view mode)
  public onChatboxLayoutChange(payload: { active: boolean; mode: string }) {
    this.isChatboxSplitActive = payload.active && payload.mode === 'split';
  }

  public getAppMenuBarStatus() {
    if (localStorage.getItem('isExpandAppMenuBar') === 'false') {
      this.isExpandAppMenuBar = false;
    } else {
      this.isExpandAppMenuBar = true;
    }
  }

  /**
   * Get the Shopify host parameter, preferring instance variable,
   * then URL query, then sessionStorage
   */
  private getShopifyHost(): string {
    if (this.host) {
      return this.host;
    }
    const hostFromUrl = this.$route.query.host as string;
    if (hostFromUrl) {
      this.host = hostFromUrl;
      sessionStorage.setItem('shopify_host', hostFromUrl);
      return this.host;
    }
    this.host = sessionStorage.getItem('shopify_host') || '';
    return this.host;
  }

  public changeAppMenuBarStatus(isExpand: boolean) {
    this.isExpandAppMenuBar = isExpand;
  }
  public checkAthenaFinishInterval() {
    if (this.store.passedInstallingFlow === 'new') {
      return;
    }
    if (!this.store.isAthenaFinished) {
      // Clear any existing interval to prevent stacking
      if (this.athenaPollingInterval) {
        clearInterval(this.athenaPollingInterval);
        this.athenaPollingInterval = null;
      }
      localStorage.setItem('isAthenaFinished', 'true');
      this.athenaPollingInterval = window.setInterval(() => {
        if (this.store.isAthenaFinished) {
          clearInterval(this.athenaPollingInterval!);
          this.athenaPollingInterval = null;
          localStorage.setItem('isAthenaFinished', 'false');
        } else {
          this.store.fetch();
        }
      }, 60000);
    }
  }

  public async fullLoad() {
    this.isFullLoadFinished = false;
    await this.checkInstallation();
    this.isPermissionLoading = false;
    if (this.isLogined) {
      this.contentLoading = true;
      const preload = [];
      preload.push(this.tagTypes.fetch());
      if (this.activeDimensions.size() === 0) {
        preload.push(this.activeDimensions.fetch());
      }

      if (this.tags.size() === 0) {
        preload.push(this.tags.fetch());
      }
      if (this.reportMetadatas.size() === 0) {
        preload.push(this.reportMetadatas.fetch());
      }
      Promise.all(preload);
      if (this.measures.size() === 0) {
        await this.measures.fetch({ isOlap: true });
      }
      this.contentLoading = false;

      // Start checking for pending login requests
      this.startLoginRequestCheck();
    }
    this.isFullLoadFinished = true;
  }

  public doLogout() {
    removeIdentityToken();
    // Reset Mixpanel on logout to clear user data
    MixpanelInitializer.reset();
    this.checkLogin();
  }

  public setupCrisp(e: any) {
    try {
      // Hide Crisp initially
      // @ts-ignore: Unreachable code error
      this.$crisp.push(['do', 'chat:hide']);

      // Listen for when Crisp is opened
      // @ts-ignore: Unreachable code error
      this.$crisp.push(['on', 'chat:opened', () => {
        // Emit event to hide agent FAB when Crisp opens
        EventBus.$emit('crisp-opened');
      }]);

      // Listen for when Crisp is closed
      // @ts-ignore: Unreachable code error
      this.$crisp.push(['on', 'chat:closed', () => {
        // Hide Crisp and show agent FAB when Crisp is closed
        // @ts-ignore: Unreachable code error
        this.$crisp.push(['do', 'chat:hide']);
        EventBus.$emit('crisp-closed');
      }]);
    } catch {
      // do nothing
    }
  }

  public showReportSideBar() {
    return [
      'templateReports',
      'customReports',
      'customReport',
      'templateReport',
      'detailReportPage',
    ].includes(this.$route.name || '');
  }

  public checkPermission() {
    return true;
    const permissions: { [key: string]: string[] } = {
      notification: [
        'notifications',
        'notificationNewPage',
      ],
    };

    for (const permission of Object.keys(permissions)) {
      if (Object.keys(this.permission.features).includes(permission)) {
        if (permissions[permission].includes(this.$route.name || '')) {
          this.currentFeature = permission;
          return this.permission.features[permission];
        }
      }
    }

    return true;
  }

  public checkIsRequiredLogin() {
    const notRequiredLoginUrls = [
      'dataFileView',
      'slackSuccessPage',
      'statusPage',
    ];
    if (notRequiredLoginUrls.includes(this.$route.name || '')) {
      this.isRequiredLogin = false;
    } else {
      this.isRequiredLogin = true;
    }
  }

  /**
   * Phase 1: Early Mixpanel SDK initialization
   *
   * Initializes the Mixpanel SDK immediately so it's ready to track events
   * even before store data is loaded. This prevents tracking errors in components
   * that load early (like SubscriptionConfirm).
   *
   * Called at the beginning of created() lifecycle hook.
   */
  private initializeMixpanelSdk(): void {
    try {
      const token = process.env.VUE_APP_MIXPANEL_TOKEN;
      if (!token) {
        console.warn('[App] Mixpanel token not found, skipping SDK initialization');
        return;
      }

      // Detect embedded mode
      let isEmbedded = false;
      try {
        isEmbedded = isShopifyEmbedded();
      } catch (error) {
        // Not embedded, continue
        isEmbedded = false;
      }

      // Initialize Mixpanel SDK early
      mixpanel.init(token, {
        debug: process.env.NODE_ENV !== 'production',
        ignore_dnt: process.env.NODE_ENV !== 'production', // Respect DNT in production
        disable_persistence: isEmbedded
      });

      console.log('[App] Mixpanel SDK initialized early (before store load)');
    } catch (error) {
      console.warn('[App] Failed to initialize Mixpanel SDK:', error);
    }
  }

  /**
   * Phase 2: Mixpanel user identification and properties
   *
   * Called after store data is loaded to identify the user and set super properties.
   * The SDK must already be initialized via initializeMixpanelSdk() first.
   */
  private initializeMixpanel(): void {
    if (!this.store) {
      return;
    }
    // Initialize Mixpanel with safe error handling
    const initialized = MixpanelInitializer.initialize(this.store, {
      debug: process.env.NODE_ENV !== 'production'
    });
    
    if (initialized) {
      // Set super properties that will be included with every event
      MixpanelInitializer.setSuperProperties({
        app_version: this.store.merchantAppVersion,
        system_version: this.store.systemAppVersion,
        athena_enabled: this.store.enableAthena,
        store_type: this.store.storeType || 'standard',
        etl_status: this.store.etlStatus,
        is_data_ready: this.isDataReady,
        environment: process.env.NODE_ENV
      });
      
      // Track successful login
      MixpanelInitializer.trackEvent('User Login Success', {
        login_method: this.isLoggedInFromHades ? 'hades' : 'standard',
        has_slack: this.store.slackConnected,
        has_channel: this.store.channelConnected
      });
    }
  }

  public async getStoreDetail() {
    await this.store.fetch();
    EventBus.$emit('store-detail-fetched');
    // Initialize permission service right after store fetch
    await this.initializePermissionService();
    localStorage.setItem('currency', this.store.information.currency);
    // Store the Shopify store name for use in other components
    if (this.store.shopName) {
      localStorage.setItem('shopifyStoreName', this.store.shopName);
    }
    if (this.store.etlStatus !== 'ready') {
      this.isDataReady = false;
    }
    this.shopifyName = this.store.shopName;

    // Init cache for OLAPs all stores which id is greater than betaStoreId
    // TODO: Need to update for all merchants
    const betaStoreId = 1;
    const isBetaStore = betaStoreId < this.store.id;

    if (isBetaStore && !this.olapsWarmedThisAppInstance) { // Check the instance flag
      try {
        // console.log('Warming OLAPs for this app instance...'); // Optional: for debugging
        this.olapsWarmedThisAppInstance = true; // Set the flag for this instance
        await this.store.warmOlaps();
      } catch (error) {
        console.error('Failed to warm OLAPs:', error);
        // Optionally, handle the error. You might choose not to set the flag
        // if it fails, allowing another attempt if getStoreDetail is called again.
      }
    } else if (isBetaStore && this.olapsWarmedThisAppInstance) {
      // console.log('OLAPs already warmed for this app instance.'); // Optional: for debugging
    }
    // End init

    if (this.store.merchantAppVersion !== this.store.systemAppVersion) {
      this.needUpgrade = true;
    }

    if (this.store.needsInventoryScope()) {
      if (this.getCanShowUpgradeVersion) {
        this.needUpgrade = true;
      }
    }

    if (this.store.passedInstallingFlow !== 'new') {
      const onboardUrls = [
        'onboard',
        'onboardContact',
        'onboardReport',
        'onboardPurpose',
        'onboardComplete',
        'onboardPricing',
      ];
      if (onboardUrls.includes(this.$route.name || '')) {
        if (isShopifyEmbedded()) {
          this.$router.push({ name: 'home' });
        } else {
          window.location.assign(`https://admin.shopify.com/store/${this.store.shopName.split('.')[0]}/apps/${process.env.VUE_APP_NAME}`);
        }
      }

    } else {
      if (this.$route.name === 'SetupGuide') {
        const storeModel = new StoreModel();
        await storeModel.fetch();
        this.store = storeModel;
        // Store the Shopify store name for use in other components
        if (this.store.shopName) {
          localStorage.setItem('shopifyStoreName', this.store.shopName);
        }
      } else {
        if (!this.installationUrls.includes(this.$route.name || '')) {
          this.$router.push({ name: 'onboardPricing' });
        }
      }
    }
    try {
      if (process.env.VUE_APP_ENVIRONMENT === 'production' && window.location.hostname !== 'demo.assisty.ai' && !window.location.hostname.endsWith('.trycloudflare.com')) {
        try {
          LogRocket.identify(`${this.store.id}`, {
            name: this.store.information.myshopify_domain,
            email: this.store.information.email,
          });
        } catch {
          // skipped
        }
      }
      if (!this.isLoggedInFromHades && this.store?.config?.email) {
        // @ts-ignore: Unreachable code error
        this.$crisp.push([
          'set', 'user:email', [this.store.config.email],
        ]);
        if (this.store?.information?.name) {
          // @ts-ignore: Unreachable code error
          this.$crisp.push([
            'set', 'user:nickname', [this.store.information.name],
          ]);
        }
        // @ts-ignore: Unreachable code error
        this.$crisp.push([
          'set',
          'session:data',
          [
            [
              ['assisty_plan', this.store.currentPackage.name],
              ['assisty_plan_actived_at', this.store.currentPackage.activatedAt || this.store.currentPackage.createdAt],
              ['shopify_plan', this.store.information.plan_display_name],
              ['assisty_installed_at', this.store.installedAt],
              ['id', this.store.id],
              ['url', this.store.information.domain],
              ['email', this.store.information.email],
              ['name', this.store.information.myshopify_domain],
              ['hades_url', `https://hades.assisty.ai/stores/${this.store.id}`],
              ['is_trial', this.store.isTrial()],
              ['current_step', this.store.currentStep],
              ['store_admin_url', this.store.information.myshopify_domain.replace('.myshopify.com', '')],
            ],
          ],
        ]);
        // @ts-ignore: Unreachable code error
        window.Appcues.identify(
          this.store.id, // unique, required
          {
            assisty_plan: this.store.currentPackage.name,
            assisty_plan_actived_at: this.store.currentPackage.activatedAt,
            shopify_plan: this.store.information.plan_display_name,
            assisty_installed_at: this.store.installedAt,
            id: this.store.id,
            url: this.store.information.domain,
            email: this.store.information.email,
            name: this.store.information.myshopify_domain,
            is_trial: this.store.isTrial(),
            current_step: this.store.currentStep,
          },
        );
      }
    } catch (err: any) {
      // do nothing
    }
  }

  /**
   * Handle when user skips the upgrade dialog
   */
  public handleUpgradeSkipped() {
    // Mark this session as having skipped the upgrade
    this.needUpgrade = false;


    // Reload the main content to ensure components initialize properly
    this.mainRandomKey = Math.random();

    // Show a notification to inform the user about limited functionality
    EventBus.$emit('show-snackbar', {
      message: 'Some features may be limited until you upgrade the app permissions.',
      color: 'warning',
    });
  }

  /**
   * Check for pending login requests
   */
  public async checkForPendingLoginRequests() {
    if (!this.store.id) {
      return;
    }

    try {
      const loginRequestList = new LoginRequestList();
      const request = await loginRequestList.checkForPending(this.store.id);

      if (request) {
        this.pendingLoginRequest = request;
        this.showLoginRequestPopup = true;
      }
    } catch (error: any) {
      console.error('Error checking for pending login requests:', error);
    }
  }

  /**
   * Start periodic check for login requests
   */
  public startLoginRequestCheck() {
    // Check only once on app init, no interval
    this.checkForPendingLoginRequests();
  }

  /**
   * Handle when customer responds to login request
   */
  public onLoginRequestResponded(request: LoginRequestModel) {
    this.pendingLoginRequest = null;
    this.showLoginRequestPopup = false;
    // No need to check again - only check on app init
  }

  /**
   * Show snackbar notification
   */
  public showSnackbar(config: { message: string; color: string }) {
    this.appSnackbar = true;
    this.appSnackbarMessage = config.message;
    this.appSnackbarColor = config.color;
  }

  /**
   * Initialize the permission service for feature access control
   */
  public async initializePermissionService() {
    if (this.store) {
      // Set the initialization flag if not already set
      if (Vue.prototype.$permissionsInitializing === undefined) {
        Vue.prototype.$permissionsInitializing = true;
      }

      // Make store globally available for route guards immediately
      Vue.prototype.$appStore = this.store;

      try {
        // Make sure rules are loaded
        if (!this.store.rules || !this.store.rules.items || this.store.rules.items.length === 0) {
          await this.store.getRules();
        }
        // Initialize permission service
        this.permissionService = providePermission(this.store);
        this.permissionServiceInitialized = true;

        // Clear initialization flag
        Vue.prototype.$permissionsInitializing = false;

        // Emit event to notify components that permissions are ready
        EventBus.$emit('permissions-ready');
      } catch (error: any) {
        console.error('Failed to initialize permission service:', error);
        // Even on error, we should clear the initializing flag to avoid blocking navigation
        Vue.prototype.$permissionsInitializing = false;

        // Show error message to user
        EventBus.$emit('show-snackbar', {
          message: 'Could not load permissions. Some features may be unavailable.',
          color: 'error',
          timeout: 8000,
        });
      }
    }
  }

  // Add or modify beforeDestroy
  public beforeDestroy() {
    if (this.athenaPollingInterval) {
      clearInterval(this.athenaPollingInterval);
      this.athenaPollingInterval = null;
    }
    EventBus.$off('update-new-store-data');
    EventBus.$off('full-load-app');
    EventBus.$off('check-athena-finish-interval');
    // EventBus.$off("pin-menu", this.pinMenu); // Ensure this is removed if it existed
    EventBus.$off('redirect-to-google-auth');
    EventBus.$off('show-snackbar');
    EventBus.$off('clear-cache');
    EventBus.$off('permission-denied');
    EventBus.$off('check-permission');
    EventBus.$off('permissions-ready'); // This was .$on, should be .$off if it had a handler
    EventBus.$off('etl-polling-started');
    EventBus.$off('etl-polling-stopped');
    EventBus.$off('vertical-menu-pinned', this.handleVerticalMenuPinned);
    EventBus.$off('app:chatbox-layout-change', this.onChatboxLayoutChange);
  }

  private async checkLogin() {
    this.loading = true;
    if (Object.keys(this.$route.query).includes('hades')) {
      this.isLoggedInFromHades = true;
      localStorage.setItem('loggedInFromHades', 'true');
      let res: any;
      try {
        res = await get(`/hades/verify`, { token: this.$route.query.hades });
        const token = res.token;
        if (token) {
          setIdentityToken(token, res.store.shop_name);
          this.isLogined = true;
          const query = Object.assign({}, this.$route.query);
          delete query.hades;
          this.$router.replace({ query });
          await this.getStoreDetail();
          this.error = false;
        } else {
          this.isLogined = false;
        }
      } catch (err: any) {
        if (err.response.status === 401) {
          removeIdentityToken();
        }
        this.isLogined = false;
      }
    }

    if (verifyIdentityToken()) {
      if (this.$route.query.shop) {
        if (localStorage.getItem('shopify_store') !== this.$route.query.shop) {
          this.isLogined = false;
          removeIdentityToken();
          return;
        }
      }
      try {
        const verifyResult: any = await get('/token/verify');
        if (verifyResult.token) {
          setIdentityToken(verifyResult.token, verifyResult.store.shop_name);
          if (this.store?.config?.contactEmail) {
            localStorage.setItem('current_loggedin_email', this.store.config.contactEmail);
          }
        }
        this.isLogined = true;
        this.removeShopifyQueries();
        await this.getStoreDetail();
      } catch (err: any) {
        console.log(err);
        if (err.response?.status === 401) {
          removeIdentityToken();
        }
        this.isLogined = false;
      }
    } else {
      this.isLogined = false;
    }
    if (this.isLogined) {
      //if (!this.isLoggedInFromHades) {
        // Legacy code - kept commented for reference
      //}

      const redirectUri = localStorage.getItem('redirect_uri');
      if (redirectUri) {
        localStorage.removeItem('redirect_uri');
        this.$router.push(redirectUri);
      }

      return;
    } else {
      if (this.$route.path !== '/') {
        this.referenceRoute = this.$route;
        // this.$router.push({ name: 'home', query: this.$route.query });
      }
    }
    this.loading = false;
  }

  private removeShopifyQueries() {
    const query = Object.assign({}, this.$route.query);
    delete query.hmac;
    delete query.host;
    delete query.session;
    delete query.shop;
    delete query.timestamp;
    this.$router.replace({ query });
  }

  private async createStore() {
    this.creating = true;
    const query = this.$route.query;
    const data: { [key: string]: any } = {};
    for (const key of Object.keys(query)) {
      data[key] = query[key];
    }
    try {
      const res: any = await get(`/shopify/callback`, data);
      const token = res.token;
      if (token) {
        setIdentityToken(token, res.store.shop_name);
        window.location.assign(`https://admin.shopify.com/store/${data.shop.split('.')[0]}/apps/${process.env.VUE_APP_NAME}`);
        this.isLogined = true;
        this.error = false;
        this.installing = false;
        this.creating = false;
      }
    } catch (e: any) {
      this.error = true; this.errorMessage = e.response.data.message; this.creating = false;
      return;
    }
  }

  private async getClientIP() {
    try {
      const axios = require('axios').default;
      const options = {
        method: 'get',
        url: 'https://api.ipify.org?format=json',
      };
      const ip = await axios.request(options).then((response: any) => {
        return response.data.ip;
      });
      return ip;
    } catch (error: any) {
      return '';
    }
  }

  private async checkInstallation() {
    this.error = false;
    this.installing = false;
    this.checkInstalling = true;
    await this.checkLogin();
    if (this.isLogined) {
      this.error = false;
      this.loading = false;
      this.installing = false;
      this.checkInstalling = false;
      return;
    }
    const query = this.$route.query;
    const data: { [key: string]: any } = {};
    for (const key of Object.keys(query)) {
      data[key] = query[key];
    }
    try {
      const res: any = await get(`/shopify`, data);
      const installUrl = res.install_url;
      const token = res.token;
      if (installUrl) {
        // If your app is embedded inside Shopify, use App Bridge to redirect
        if (isShopifyEmbedded()) {
          const host = this.getShopifyHost();
          if (host) {
            this.app = createApp({
              apiKey: this.apiKey,
              host: host,
            });
            Redirect.create(this.app).dispatch(Redirect.Action.REMOTE, installUrl);
          }
          // Otherwise, redirect using the `window` object
        } else {
          window.location.assign(installUrl);
        }
        this.error = false;
        this.installing = true;
      }
      if (token) {
        setIdentityToken(token, res.store.shop_name);
        this.isLogined = true;
        this.removeShopifyQueries();
        if (isShopifyEmbedded()) {
          const host = this.getShopifyHost();
          if (host) {
            this.app = createApp({
              apiKey: this.apiKey,
              host: host,
            });
          }
        }
        await this.getStoreDetail();
        if (this.referenceRoute) {
          this.$router.push(this.referenceRoute);
          this.removeShopifyQueries();
        }
        this.error = false;
        this.installing = false;
      }
    } catch (e) {
      this.error = true;
      if (getIdentityToken()) {
        this.errorMessage = 'There are some error while installing. Please try again later';
      } else {
        this.errorMessage = 'You are not logged in yet. Please go to Shopify Store\'s Apps to access Assisty.';
      }
      this.installing = false;
    }
    this.checkInstalling = false;
  }

  @Watch('store.isAthenaReady')
  public onAthenaReadyChange(newVal: boolean, oldVal: boolean) {
    if (newVal && !oldVal && this.$route.name === 'dataImporting') {
      this.showDataReadyModal = true;
      const eventHandler = new EventHandler({ store: this.store });
      eventHandler.track('Data Ready Modal Shown', {
        store: this.store.shopName,
      });
    }
  }

  public onDataReadyDismiss() {
    this.showDataReadyModal = false;
    if (this.$route.path !== '/') {
      this.$router.push('/');
    }
  }

  @Watch('$route', { immediate: true, deep: true })
  private async onUrlChange(newVal: any) {
    this.isCallbackRequest = this.$route.name === 'callback';
    if (isShopifyEmbedded() && this.app) {
      History.create(this.app).dispatch(History.Action.REPLACE, this.$route.fullPath);
    }
    if (this.isLogined && this.isRequiredLogin) {
      try {
        if (!this.isLoggedInFromHades) {
          if (this.$route.name !== 'customReport'
            && this.$route.name !== 'templateReport'
          ) {
            const eventHandler = new EventHandler({
              store: this.store,
            });
            eventHandler.track(`view ${this.$route.name} page`);
            this.store.sendEvent('VIEW_PAGE', {
              name: this.$route.name,
              path: this.$route.path,
            });
          }
        }
      } catch (e) {
        // ignore this
      }
      this.getStoreDetail();
    }
  }

  private clearCache() {
    for (const key of Object.keys(localStorage)) {
      if (key.split('_')[0] === 'cache') {
        localStorage.removeItem(key);
      }
    }
  }
}
